import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { apiClient } from "@shared/services/api";
import { ADMIN_CATEGORY_KEYS } from "@shared/constants/queryKeys";
import type { PaginatedResponse, PaginationMeta } from "@shared/types";
import type { Category } from "@admin/types/category";

type CategoryListResponse = {
  success?: boolean;
  message?: string;
  data:
    | Category[]
    | {
        items: Category[];
        pagination?: PaginationMeta;
      };
  pagination?: PaginationMeta;
  meta?: PaginationMeta;
};

export function useCategories(params: { page?: number; limit?: number; search?: string } = {}) {
  const { page = 1, limit = 10, search } = params;

  return useQuery({
    queryKey: ADMIN_CATEGORY_KEYS.list({ page, limit, search: search ?? "" }),
    queryFn: async (): Promise<PaginatedResponse<Category>> => {
      const searchParams = new URLSearchParams({
        page: String(page),
        limit: String(limit),
      });

      if (search) {
        searchParams.append("name", search);
      }

      const response = await apiClient.get<CategoryListResponse>(
        `/category?${searchParams.toString()}`
      );

      const rawData = response.data;
      const items = Array.isArray(rawData)
        ? rawData
        : rawData?.items ?? [];

      const meta =
        response.pagination ??
        response.meta ??
        (Array.isArray(rawData) ? undefined : rawData?.pagination);

      const computedLimit = meta?.limit ?? limit;
      const computedPage = meta?.page ?? page;
      const computedTotal = meta?.total ?? items.length;
      const computedTotalPages =
        meta?.totalPages ??
        Math.max(1, Math.ceil(computedTotal / computedLimit));

      const pagination: PaginationMeta = {
        total: computedTotal,
        page: computedPage,
        limit: computedLimit,
        totalPages: computedTotalPages,
        hasNext: meta?.hasNext ?? computedPage < computedTotalPages,
        hasPrev: meta?.hasPrev ?? computedPage > 1,
      };

      return {
        success: response.success ?? true,
        message: response.message,
        data: items,
        pagination,
      };
    },
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
}

